import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight, FileText } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";

const SITE = "https://lagooncityrealestate.ca";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/newsletters")
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The Lagoon City Journal",
    url: `${SITE}/blog`,
    description: "Monthly Lagoon City market reports, waterfront listings roundups and community notes from Joselle Alves, Broker.",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: `${p.month_name} ${p.year} — Lagoon City Market Report`,
      datePublished: p.created_at,
      url: `${SITE}/blog/${p.newsletter_id}`,
    })),
  };

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>Lagoon City Market Reports & Journal | Joselle Alves, Broker</title>
        <meta name="description" content="Monthly Lagoon City real estate market reports, waterfront listing roundups, and community notes from Joselle Alves — Broker for Lagoon City, Brechin & Lake Simcoe." />
        <link rel="canonical" href={`${SITE}/blog`} />
        <meta property="og:title" content="Lagoon City Market Reports & Journal" />
        <meta property="og:description" content="Monthly Lagoon City real estate market reports and community notes from Joselle Alves, Broker." />
        <meta property="og:url" content={`${SITE}/blog`} />
        <meta property="og:image" content={`${SITE}/assets/joselle-portrait.jpg`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <PageHeader />

      <main className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">The Journal</p>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Lagoon City <em className="italic text-[#E890A7]">market reports</em>
          </h1>
          <p className="mt-5 max-w-xl text-slate-400 font-light">
            Every month Joselle publishes the pulse of the Lagoon City waterfront market — fresh listings,
            price trends and life around the canals. Read any edition below.
          </p>
        </motion.div>

        {loading ? (
          <div data-testid="blog-loading" className="mt-16 flex items-center gap-3 text-slate-500">
            <Loader2 className="animate-spin" size={18} /> Loading the journal…
          </div>
        ) : posts.length === 0 ? (
          <p data-testid="blog-empty" className="mt-16 text-slate-500">The first edition is on its way — check back soon.</p>
        ) : (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
            {posts.map((p, i) => (
              <motion.div
                key={p.newsletter_id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
              >
                <Link
                  data-testid={`blog-card-${p.newsletter_id}`}
                  to={`/blog/${p.newsletter_id}`}
                  className="group block rounded-3xl border border-[#E890A7]/12 bg-[#151218] p-7 hover:border-[#E890A7]/40 transition-colors duration-300 h-full"
                >
                  <FileText size={20} className="text-[#E890A7]" />
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mt-5">
                    {p.month_name} {p.year}
                  </p>
                  <h2 className="font-display text-2xl mt-2 leading-snug">
                    Lagoon City Market Report
                  </h2>
                  <p className="text-sm text-slate-400 mt-3 font-light line-clamp-2">
                    {p.title} — listings, market stats and community notes.
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#F2A7BA] group-hover:gap-3 transition-all">
                    Read edition <ArrowUpRight size={15} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
